import { useEffect, type RefObject } from "react";

const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__coneyTurnstileReady";
const TEST_SITEKEY = "1x00000000000000000000AA";
const PROD_SITEKEY = "0x4AAAAAAErj7gS_-w4YLl6R";
const ACTION = "sign";
const CARD_WAIT_MS = 30_000;
const CORNER_WAIT_MS = 60_000;
const COMPACT_BELOW = 300;

type Turnstile = {
  render(container: HTMLElement, options: Record<string, unknown>): string | undefined;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
    __coneyTurnstileReady?: () => void;
  }
}

type Waiter = (token: string | null) => void;

type Widget = {
  id: string | null;
  token: string | null;
  spent: boolean;
  waiters: Waiter[];
};

export function sitekey(): string {
  return (
    import.meta.env.VITE_TURNSTILE_SITEKEY || (import.meta.env.DEV ? TEST_SITEKEY : PROD_SITEKEY)
  );
}

let loading: Promise<Turnstile | null> | null = null;

function loadTurnstile(): Promise<Turnstile | null> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<Turnstile | null>((resolve) => {
    window.__coneyTurnstileReady = () => resolve(window.turnstile ?? null);
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onerror = () => {
      loading = null;
      resolve(null);
    };
    document.head.append(script);
  });
  return loading;
}

function settle(widget: Widget, token: string | null): void {
  for (const waiter of widget.waiters.splice(0)) waiter(token);
}

async function render(container: HTMLElement, submissionId: string): Promise<Widget | null> {
  const turnstile = await loadTurnstile();
  if (!turnstile || !container.isConnected) return null;
  const widget: Widget = { id: null, token: null, spent: false, waiters: [] };
  const width = container.clientWidth;
  widget.id =
    turnstile.render(container, {
      sitekey: sitekey(),
      action: ACTION,
      cData: submissionId,
      appearance: "interaction-only",
      size: width > 0 && width < COMPACT_BELOW ? "compact" : "flexible",
      "response-field": false,
      callback: (token: string) => {
        widget.token = token;
        widget.spent = false;
        settle(widget, token);
      },
      "expired-callback": () => {
        widget.token = null;
      },
      "error-callback": () => {
        widget.token = null;
        settle(widget, null);
      },
      "timeout-callback": () => {
        widget.token = null;
        settle(widget, null);
      },
    }) ?? null;
  return widget;
}

function take(widget: Widget, waitMs: number): Promise<string | null> {
  if (widget.token && !widget.spent) {
    widget.spent = true;
    return Promise.resolve(widget.token);
  }
  if (widget.spent && widget.id) {
    widget.spent = false;
    widget.token = null;
    window.turnstile?.reset(widget.id);
  }
  return new Promise((resolve) => {
    const waiter: Waiter = (token) => {
      window.clearTimeout(timer);
      if (token) widget.spent = true;
      resolve(token);
    };
    const timer = window.setTimeout(() => {
      widget.waiters = widget.waiters.filter((w) => w !== waiter);
      resolve(null);
    }, waitMs);
    widget.waiters.push(waiter);
  });
}

function dispose(widget: Widget | null): void {
  if (!widget) return;
  settle(widget, null);
  if (widget.id) window.turnstile?.remove(widget.id);
}

const mounted = new Map<string, Promise<Widget | null>>();

export function useProof(container: RefObject<HTMLElement | null>, submissionId: string | null) {
  useEffect(() => {
    const element = container.current;
    if (!submissionId || !element) return;
    const pending = render(element, submissionId);
    mounted.set(submissionId, pending);
    return () => {
      if (mounted.get(submissionId) === pending) mounted.delete(submissionId);
      void pending.then(dispose);
    };
  }, [container, submissionId]);
}

let corner: Promise<string | null> = Promise.resolve(null);

function cornerProof(submissionId: string): Promise<string | null> {
  const next = corner.then(async () => {
    const container = document.createElement("div");
    container.className = "proof-corner";
    document.body.append(container);
    let widget: Widget | null = null;
    try {
      widget = await render(container, submissionId);
      return widget ? await take(widget, CORNER_WAIT_MS) : null;
    } finally {
      dispose(widget);
      container.remove();
    }
  });
  corner = next.catch(() => null);
  return next;
}

export async function proofFor(submissionId: string): Promise<string | null> {
  const widget = await mounted.get(submissionId);
  if (widget) return take(widget, CARD_WAIT_MS);
  return cornerProof(submissionId);
}
